import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Heading2, Link2, Quote, Undo, Redo } from "lucide-react";

/**
 * Lightweight contenteditable rich-text editor.
 * Stores HTML in `value`. Triggers onChange(html).
 */
export default function RichTextEditor({ value, onChange, placeholder = "Write here..." }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    onChange?.(ref.current?.innerHTML || "");
  };

  const onInput = () => onChange?.(ref.current?.innerHTML || "");

  const linkPrompt = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  const Btn = ({ icon: Icon, label, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="border border-input rounded-md overflow-hidden bg-white">
      <div className="flex items-center gap-1 px-2 py-1 border-b border-input bg-muted/50">
        <Btn icon={Bold} label="Bold" onClick={() => exec("bold")} />
        <Btn icon={Italic} label="Italic" onClick={() => exec("italic")} />
        <Btn icon={Heading2} label="Heading" onClick={() => exec("formatBlock", "<h2>")} />
        <Btn icon={Quote} label="Quote" onClick={() => exec("formatBlock", "<blockquote>")} />
        <span className="w-px h-5 bg-border mx-1" />
        <Btn icon={List} label="Bulleted list" onClick={() => exec("insertUnorderedList")} />
        <Btn icon={ListOrdered} label="Numbered list" onClick={() => exec("insertOrderedList")} />
        <Btn icon={Link2} label="Insert link" onClick={linkPrompt} />
        <span className="w-px h-5 bg-border mx-1" />
        <Btn icon={Undo} label="Undo" onClick={() => exec("undo")} />
        <Btn icon={Redo} label="Redo" onClick={() => exec("redo")} />
      </div>
      <div
        ref={ref}
        contentEditable
        data-testid="rich-text-editor"
        onInput={onInput}
        suppressContentEditableWarning
        className="prose prose-sm max-w-none p-3 min-h-[180px] focus:outline-none [&_h2]:font-serif [&_h2]:text-xl [&_h2]:mt-3 [&_h2]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:underline"
        data-placeholder={placeholder}
      />
    </div>
  );
}
